import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../lib/api';
import { ShieldCheck, SpinnerGap, WarningCircle } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';

export default function SSOCallbackPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const code = searchParams.get('code');
    const errorParam = searchParams.get('error');
    const errorDesc = searchParams.get('error_description');

    if (errorParam) {
      setStatus('error');
      setError(errorDesc || errorParam || 'Azure AD authentication failed');
      return;
    }

    if (!code) {
      setStatus('error');
      setError('No authorization code received from Azure AD');
      return;
    }

    handleCallback(code);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps
  // handleCallback, login, navigate are stable refs — intentionally excluded

  const handleCallback = async (code) => {
    try {
      // Retrieve the PKCE code verifier from session storage
      const codeVerifier = sessionStorage.getItem('pkce_code_verifier');
      
      if (!codeVerifier) {
        setStatus('error');
        setError('PKCE code verifier not found. Please try signing in again.');
        return;
      }

      // Get SSO config to know tenant_id and client_id
      const configRes = await API.get('/auth/sso/token-config');
      const { tenant_id, client_id, redirect_uri } = configRes.data;

      // Validate tenant_id is a valid UUID to prevent URL injection
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!tenant_id || !uuidRegex.test(tenant_id)) {
        setStatus('error');
        setError('Invalid SSO configuration: tenant ID format is not valid.');
        return;
      }

      // Exchange code for tokens directly with Azure AD (SPA PKCE flow)
      const tokenUrl = `https://login.microsoftonline.com/${tenant_id}/oauth2/v2.0/token`;
      
      const tokenParams = new URLSearchParams({
        client_id: client_id,
        code: code,
        redirect_uri: redirect_uri,
        grant_type: 'authorization_code',
        code_verifier: codeVerifier,
        scope: 'openid profile email User.Read'
      });

      const tokenResponse = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: tokenParams.toString()
      });

      if (!tokenResponse.ok) {
        const errorData = await tokenResponse.json();
        throw new Error(errorData.error_description || errorData.error || 'Token exchange failed');
      }

      const tokens = await tokenResponse.json();
      
      // Clean up the code verifier from session storage
      sessionStorage.removeItem('pkce_code_verifier');

      // Send access token to backend to create/authenticate user
      const res = await API.post('/auth/sso/verify', { 
        access_token: tokens.access_token 
      });
      
      login(res.data.user);
      setStatus('success');
      setTimeout(() => navigate('/'), 1000);
    } catch (err) {
      setStatus('error');
      setError(err.response?.data?.detail || err.message || 'SSO authentication failed. Please try again.');
      // Clean up on error too
      sessionStorage.removeItem('pkce_code_verifier');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8">
      <div className="max-w-md w-full text-center" data-testid="sso-callback-page">
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-sm bg-orange-500 flex items-center justify-center">
            <ShieldCheck size={22} className="text-white" strokeWidth={2} />
          </div>
          <h1 className="text-xl font-black text-navy-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            IAG Platform
          </h1>
        </div>

        {status === 'processing' && (
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-8" data-testid="sso-processing">
            <SpinnerGap size={40} className="mx-auto mb-4 text-orange-500 animate-spin" />
            <h2 className="text-lg font-bold text-navy-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Completing Sign-In
            </h2>
            <p className="text-sm text-navy-500">
              Exchanging authorization with Azure AD...
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-white border border-emerald-200 rounded-sm shadow-sm p-8" data-testid="sso-success">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-navy-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Signed In Successfully
            </h2>
            <p className="text-sm text-navy-500">
              Redirecting to dashboard...
            </p>
          </div>
        )}

        {status === 'error' && (
          <div className="bg-white border border-red-200 rounded-sm shadow-sm p-8" data-testid="sso-error">
            <WarningCircle size={40} className="mx-auto mb-4 text-red-500" />
            <h2 className="text-lg font-bold text-navy-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Authentication Failed
            </h2>
            <p className="text-sm text-red-600 mb-4">
              {error}
            </p>
            <div className="flex items-center justify-center gap-3">
              <Button onClick={() => navigate('/login')} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm"
                data-testid="sso-back-to-login">
                Back to Login
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
